import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Row, Col, Typography } from 'antd';
import ScrollReveal from '../../components/ScrollReveal';
import productsHero from '../../assets/images/product-courtyard-hero.png';
import advantageBg1 from '../../assets/images/product_images/cut_01_compressed.jpg';
import advantageBg2 from '../../assets/images/product_images/cut_02_compressed.jpg';
import advantageBg3 from '../../assets/images/product_images/cut_03_compressed.jpg';
import advantageBg4 from '../../assets/images/product_images/cut_04_compressed.jpg';
import inventoryImg from '../../assets/images/product-inventory-illustration.png';
import accountingImg from '../../assets/images/product-accounting-illustration.png';
import inventoryIcon from '../../assets/images/icon/cut_01_compressed.webp';
import accountingIcon from '../../assets/images/icon/cut_02_compressed.webp';

const { Title } = Typography;

const advantages = [
  {
    title: '高性价比',
    subtitle: '按需订阅 · 成本可控',
    bg: advantageBg1,
  },
  {
    title: '持续迭代',
    subtitle: '统一升级 · 稳定演进',
    bg: advantageBg2,
  },
  {
    title: '业财一体',
    subtitle: '一次录入 · 全程协同',
    bg: advantageBg3,
  },
  {
    title: '经营洞察',
    subtitle: '数据清晰 · 决策有据',
    bg: advantageBg4,
  },
];

const flowTabs = [
  {
    key: 'inventory',
    label: '进销存',
    desc: '采购、销售、库存一体协同',
    icon: inventoryIcon,
    iconClass: 'pc-flow-info-icon--blue',
  },
  {
    key: 'accounting',
    label: '云会计',
    desc: '凭证、账簿、报表自动衔接',
    icon: accountingIcon,
    iconClass: 'pc-flow-info-icon--cyan',
  },
];

// 能力图谱 - 四个能力板块围绕中间的业财一体化中心
const capabilities = [
  {
    key: 'business',
    position: 'tl',
    color: 'green',
    title: '业务经营',
    items: ['采购订货', '销售履约', '库存协同', '客商往来'],
  },
  {
    key: 'collab',
    position: 'tr',
    color: 'orange',
    title: '业财协同',
    items: ['单据驱动', '规则映射', '自动核销', '账实核对'],
  },
  {
    key: 'finance',
    position: 'bl',
    color: 'blue',
    title: '财务核算',
    items: ['自动凭证', '总账报表', '资金结算', '税务管理'],
  },
  {
    key: 'insight',
    position: 'br',
    color: 'purple',
    title: '数据洞察',
    items: ['销售分析', '库存周转', '往来风险', '利润分析'],
  },
];

const Products = () => {
  const [activeTab, setActiveTab] = useState('inventory');
  const location = useLocation();

  // 从 URL hash(如 /products#architecture)滚动到对应章节
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (!hash) return;
    // 等 DOM 渲染完毕再滚动
    const timer = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) {
        const headerHeight = 64;
        const top =
          el.getBoundingClientRect().top + window.pageYOffset - headerHeight;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }, 80);
    return () => clearTimeout(timer);
  }, [location.hash]);

  return (
    <div className="page-products">
      <section
        className="products-hero"
        style={{ backgroundImage: `url(${productsHero})` }}
      >
        <div className="products-hero-content">
          <Title level={1} className="products-hero-title">
            产品服务
          </Title>
        </div>
      </section>

      <section className="advantage-section" id="advantage">
        <div className="advantage-section-inner">
          <ScrollReveal>
            <div className="advantage-section-head">
              <Title level={2} className="advantage-section-title">
                产品优势
              </Title>
              <div className="advantage-section-underline" />
            </div>
          </ScrollReveal>

          {/* 4 张优势卡:用 stagger 让它们依次出场 */}
          <ScrollReveal delay={100}>
            <Row gutter={[20, 20]}>
              {advantages.map((item, idx) => (
                <Col xs={12} sm={12} md={6} key={idx}>
                  <div
                    className="advantage-card"
                    style={{ '--advantage-bg': `url(${item.bg})` }}
                  >
                    <div className="advantage-card-content">
                      <h3 className="advantage-card-title">{item.title}</h3>
                      <p className="advantage-card-subtitle">{item.subtitle}</p>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </ScrollReveal>
        </div>
      </section>

      <section className="pc-flow-section">
        <div className="advantage-section-inner">
          <ScrollReveal>
            <Row gutter={[24, 24]} style={{ alignItems: 'center' }}>
              <Col xs={24} md={7}>
                <div className="pc-flow-info">
                  {flowTabs.map((tab) => (
                    <div
                      key={tab.key}
                      className={`pc-flow-info-card ${activeTab === tab.key ? 'active' : ''}`}
                      onMouseEnter={() => setActiveTab(tab.key)}
                    >
                      <div className={`pc-flow-info-icon ${tab.iconClass}`}>
                        <img src={tab.icon} alt={tab.label} className="pc-flow-icon-img" loading="lazy" decoding="async" />
                      </div>
                      <div className="pc-flow-info-text">
                        <h3>{tab.label}</h3>
                        <p>{tab.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Col>
              <Col xs={24} md={17}>
                <div className="pc-flow-image">
                  <img
                    src={activeTab === 'inventory' ? inventoryImg : accountingImg}
                    alt={activeTab === 'inventory' ? '进销存流程图' : '云会计流程图'}
                    className="pc-flow-img"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </Col>
            </Row>
          </ScrollReveal>
        </div>
      </section>

      <section className="arch-section" id="architecture">
        <div className="advantage-section-inner">
          <ScrollReveal>
            <div className="advantage-section-head">
              <Title level={2} className="advantage-section-title">产品架构</Title>
              <div className="advantage-section-underline" />
            </div>
          </ScrollReveal>

          <ScrollReveal delay={120}>
            <div className="arch-container">
            <div className="arch-layer arch-layer--entry">
              <div className="arch-layer-label">用户入口</div>
              <div className="arch-items">
                <div className="arch-item">Web 管理端</div>
                <div className="arch-item">管理驾驶舱</div>
                <div className="arch-item">工作台</div>
              </div>
            </div>

            <div className="arch-layer arch-layer--app">
              <div className="arch-layer-label">应用能力层</div>
              <div className="arch-subs">
                <div className="arch-sub arch-sub--business">
                  <div className="arch-sub-title">经营管理</div>
                  <div className="arch-sub-grid">
                    <div className="arch-item arch-item--text">销售管理</div>
                    <div className="arch-item arch-item--text">采购管理</div>
                    <div className="arch-item arch-item--text">库存管理</div>
                    <div className="arch-item arch-item--text">客户经营</div>
                    <div className="arch-item arch-item--text">商品管理</div>
                  </div>
                </div>
                <div className="arch-sub arch-sub--finance">
                  <div className="arch-sub-title">财务管理</div>
                  <div className="arch-sub-grid">
                    <div className="arch-item arch-item--text">财务核算</div>
                    <div className="arch-item arch-item--text">资金管理</div>
                    <div className="arch-item arch-item--text">税务发票</div>
                    <div className="arch-item arch-item--text">资产管理</div>
                    <div className="arch-item arch-item--text">报表分析</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="arch-layer arch-layer--business">
              <div className="arch-layer-label">业财融合能力层</div>
              <div className="arch-flow">
                <div className="arch-flow-item">业务单据中心</div>
                <div className="arch-flow-arrow">→</div>
                <div className="arch-flow-item">业务规则与核算引擎</div>
                <div className="arch-flow-arrow">→</div>
                <div className="arch-flow-item">自动凭证与财务处理</div>
                <div className="arch-flow-arrow">→</div>
                <div className="arch-flow-item">业财核对与结账控制</div>
              </div>
            </div>

            <div className="arch-layer arch-layer--platform">
              <div className="arch-layer-label">平台支撑能力层</div>
              <div className="arch-items">
                <div className="arch-item">组织权限</div>
                <div className="arch-item">流程审批</div>
                <div className="arch-item">参数配置</div>
                <div className="arch-item">档案管理</div>
                <div className="arch-item">操作审计</div>
                <div className="arch-item">系统设置</div>
              </div>
            </div>

            <div className="arch-layer arch-layer--data">
              <div className="arch-layer-label">数据能力层</div>
              <div className="arch-subs">
                <div className="arch-sub">
                  <div className="arch-sub-title">主数据</div>
                  <div className="arch-sub-desc">商品 · 客户 · 供应商</div>
                </div>
                <div className="arch-sub">
                  <div className="arch-sub-title">业务数据</div>
                  <div className="arch-sub-desc">单据 · 库存 · 资金</div>
                </div>
                <div className="arch-sub">
                  <div className="arch-sub-title">财务数据</div>
                  <div className="arch-sub-desc">凭证 · 报表 · 核算</div>
                </div>
              </div>
            </div>

            <div className="arch-layer arch-layer--saas">
              <div className="arch-layer-label">SaaS 基础能力</div>
              <div className="arch-items">
                <div className="arch-item">多租户</div>
                <div className="arch-item">数据隔离</div>
                <div className="arch-item">安全</div>
                <div className="arch-item">运维</div>
              </div>
            </div>
          </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="capability-section" id="capability">
        <div className="advantage-section-inner">
          <ScrollReveal>
            <div className="advantage-section-head">
              <Title level={2} className="advantage-section-title">能力图谱</Title>
              <div className="advantage-section-underline" />
            </div>
          </ScrollReveal>

          <ScrollReveal delay={120}>
            <div className="capability-map">
            {/* 四角能力卡片 */}
            {capabilities.map((cap) => (
              <div
                key={cap.key}
                className={`capability-card capability-card--${cap.position} capability-card--${cap.color}`}
              >
                <div className="capability-card-title">
                  <span className="capability-card-bar" />
                  {cap.title}
                </div>
                <div className="capability-card-grid">
                  {cap.items.map((item) => (
                    <div key={item} className="capability-card-item">
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* 中间的业财一体化核心 */}
            <div className="capability-core">
              <div className="capability-core-sub">平台业财云</div>
              <div className="capability-core-title">业财一体化</div>
            </div>

            {/* 四条连接线 - 从中心卡左右两侧中间伸出，然后分叉到上下卡片 */}
            <svg
              className="capability-lines"
              viewBox="0 0 1000 350"
              preserveAspectRatio="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* 左侧: 中心卡左边(x=400) → 向左 → 分叉(x=370) → 上到左上卡片右边缘(x=310) / 下到左下卡片右边缘 */}
              <path
                className="capability-line"
                d="M 400 175 L 370 175 M 370 175 L 370 50 L 310 50 M 370 175 L 370 300 L 310 300"
                fill="none"
              />
              {/* 右侧: 中心卡右边(x=600) → 向右 → 分叉(x=630) → 上到右上卡片左边缘(x=690) / 下到右下卡片左边缘 */}
              <path
                className="capability-line"
                d="M 600 175 L 630 175 M 630 175 L 630 50 L 690 50 M 630 175 L 630 300 L 690 300"
                fill="none"
              />
              {/* 只有分叉点有实心小圆点 */}
              <circle className="capability-dot-filled" cx="370" cy="175" r="4" />
              <circle className="capability-dot-filled" cx="630" cy="175" r="4" />
            </svg>
          </div>
          </ScrollReveal>
        </div>
      </section>

      {/* 核心产品能力 */}
      <section className="core-capability-section" id="core-capability">
        <div className="advantage-section-inner">
          <ScrollReveal>
            <div className="advantage-section-head">
              <Title level={2} className="advantage-section-title">核心产品能力</Title>
              <div className="advantage-section-underline" />
            </div>
          </ScrollReveal>

          <ScrollReveal delay={120}>
            <div className="core-capability-map">
            {/* 中心蓝色圆圈 - 放在底部 */}
            <div className="core-capability-center">
              <div className="core-capability-center-text">平台业财云</div>
            </div>

            {/* 16 个能力标签 - 拱形/穹顶布局:中间高两侧低,从中心圆向外辐射 */}
            {/* 第一层(顶层) - 中心高,两侧低,形成穹顶 */}
            <div className="core-capability-item core-capability-item--blue" style={{ top: '12%', left: '8%' }}>
              <span>采购管理</span>
            </div>
            <div className="core-capability-item core-capability-item--blue" style={{ top: '2%', left: '38%' }}>
              <span>销售管理</span>
            </div>
            <div className="core-capability-item core-capability-item--blue" style={{ top: '6%', left: '60%' }}>
              <span>仓库管理</span>
            </div>
            <div className="core-capability-item core-capability-item--purple" style={{ top: '14%', right: '4%' }}>
              <span>资金往来</span>
            </div>

            {/* 第二层 - 略低于第一层,同样弧形 */}
            <div className="core-capability-item core-capability-item--purple" style={{ top: '30%', left: '12%' }}>
              <span>凭证管理</span>
            </div>
            <div className="core-capability-item core-capability-item--purple" style={{ top: '20%', left: '46%' }}>
              <span>账簿报表</span>
            </div>
            <div className="core-capability-item core-capability-item--green" style={{ top: '24%', right: '24%' }}>
              <span>商品管理</span>
            </div>
            <div className="core-capability-item core-capability-item--green" style={{ top: '34%', right: '2%' }}>
              <span>客户经营</span>
            </div>

            {/* 第三层 - 继续下沉 */}
            <div className="core-capability-item core-capability-item--green" style={{ top: '46%', left: '8%' }}>
              <span>批次效期</span>
            </div>
            <div className="core-capability-item core-capability-item--green" style={{ top: '40%', left: '38%' }}>
              <span>序列号追踪</span>
            </div>
            <div className="core-capability-item core-capability-item--purple" style={{ top: '42%', right: '29%' }}>
              <span>发票税务</span>
            </div>
            <div className="core-capability-item core-capability-item--purple" style={{ top: '48%', right: '6%' }}>
              <span>固定资产</span>
            </div>

            {/* 第四层 - 紧贴中心圆两侧 */}
            <div className="core-capability-item core-capability-item--orange" style={{ top: '60%', left: '16%' }}>
              <span>出纳管理</span>
            </div>
            <div className="core-capability-item core-capability-item--orange" style={{ top: '56%', left: '40%' }}>
              <span>经营分析</span>
            </div>
            <div className="core-capability-item core-capability-item--orange" style={{ top: '58%', right: '28%' }}>
              <span>客户对账</span>
            </div>
            <div className="core-capability-item core-capability-item--cyan" style={{ top: '62%', right: '4%' }}>
              <span>智能补货</span>
            </div>

            {/* 装饰小圆点 - 沿弧线分布 */}
            <div className="core-capability-deco" style={{ top: '18%', left: '26%' }} />
            <div className="core-capability-deco core-capability-deco--lg" style={{ top: '28%', left: '54%' }} />
            <div className="core-capability-deco" style={{ top: '50%', left: '20%' }} />
            <div className="core-capability-deco core-capability-deco--lg" style={{ top: '36%', left: '74%' }} />
            <div className="core-capability-deco" style={{ top: '52%', left: '68%' }} />

            {/* SVG连接线 */}
            <svg className="core-capability-lines" viewBox="0 0 1000 700" preserveAspectRatio="none">
              <circle className="core-capability-outer-ring" cx="500" cy="500" r="320" />
            </svg>
          </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};

export default Products;
